import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import apiService from '../../services/api';
import { useToast } from '../../hooks/useToast';
import {
  JOURS,
  PlanningDayCard,
  formatAbsenceRange,
} from './equipeUi';
import {
  isCommerceClosedOnDay,
  normalizeModeHoraires,
} from '../../utils/equipePlanningUtils';

const PARIS_TZ = 'Europe/Paris';

const emptyAbsenceForm = {
  durationMode: 'fullDay',
  dateDebutYmd: '',
  dateFinYmd: '',
  partialDateYmd: '',
  partialHeureDebut: '09:00',
  partialHeureFin: '12:00',
  motif: '',
  type: '',
};

function parisYmdFromIso(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: PARIS_TZ }).format(new Date(iso));
}

function parisTimeHmFromIso(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: PARIS_TZ,
  }).format(new Date(iso));
}

function parisOffsetForYmd(ymd) {
  const probe = new Date(`${ymd}T12:00:00Z`);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PARIS_TZ,
    timeZoneName: 'shortOffset',
  }).formatToParts(probe);
  const tzName = parts.find((p) => p.type === 'timeZoneName')?.value || 'GMT+2';
  const m = tzName.match(/GMT([+-]\d+)(?::(\d+))?/i);
  if (!m) return '+02:00';
  const h = Number(m[1]);
  const mm = m[2] || '00';
  const sign = h >= 0 ? '+' : '-';
  return `${sign}${String(Math.abs(h)).padStart(2, '0')}:${mm}`;
}

/** Début de journée Paris — aligné fixture / isFullDayAbsence (00:00). */
function parisDayStartIso(ymd) {
  if (!ymd) return null;
  const offset = parisOffsetForYmd(ymd);
  const d = new Date(`${ymd}T00:00:00${offset}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Fin de journée Paris — aligné fixture (23:59). */
function parisDayEndIso(ymd) {
  if (!ymd) return null;
  const offset = parisOffsetForYmd(ymd);
  const d = new Date(`${ymd}T23:59:00${offset}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function parisWallClockIso(ymd, hhmm) {
  if (!ymd || !hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const offset = parisOffsetForYmd(ymd);
  const pad = (n) => String(n).padStart(2, '0');
  const d = new Date(`${ymd}T${pad(h)}:${pad(m)}:00${offset}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function isFullDayIndispoRange(dateDebutIso, dateFinIso) {
  if (!dateDebutIso || !dateFinIso) return false;
  const timeFmt = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: PARIS_TZ,
  });
  const start = timeFmt.format(new Date(dateDebutIso));
  const end = timeFmt.format(new Date(dateFinIso));
  return start === '00:00' && (end === '23:59' || end === '00:00');
}

function detectDurationModeFromIndispo(a) {
  if (!a?.dateDebut) return 'fullDay';
  return isFullDayIndispoRange(a.dateDebut, a.dateFin || a.dateDebut) ? 'fullDay' : 'partial';
}

function compareYmd(a, b) {
  if (!a || !b) return 0;
  return a.localeCompare(b);
}

function parseMinutes(hhmm) {
  const [h, m] = (hhmm || '').split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function formatWarningToast(warning) {
  if (!warning?.count) return null;
  return warning.warningMessage || `${warning.count} rendez-vous sont à replanifier`;
}

export default function EquipePlanningPanel({
  instructeurId,
  mode = 'planning',
  onPlanningChanged,
  wideLayout = false,
}) {
  const { toast } = useToast();
  const [modeHoraires, setModeHoraires] = useState('COMMERCE');
  const [horaires, setHoraires] = useState([]);
  const [commerceHoraires, setCommerceHoraires] = useState([]);
  const [indisponibilites, setIndisponibilites] = useState([]);
  const [planningWarning, setPlanningWarning] = useState(null);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [newPlageByDay, setNewPlageByDay] = useState({});
  const [editingHoraireId, setEditingHoraireId] = useState(null);
  const [editDraft, setEditDraft] = useState({ heureDebut: '', heureFin: '' });
  const [absenceForm, setAbsenceForm] = useState(emptyAbsenceForm);
  const [editingAbsenceId, setEditingAbsenceId] = useState(null);
  const [showAbsenceForm, setShowAbsenceForm] = useState(false);

  const notifyPlanningChanged = useCallback(() => {
    onPlanningChanged?.(instructeurId);
  }, [instructeurId, onPlanningChanged]);

  const loadPlanning = useCallback(async () => {
    if (!instructeurId) return;
    setPlanningLoading(true);
    try {
      const [config, indispoData, anomalies] = await Promise.all([
        apiService.getInstructeurHorairesConfig(instructeurId),
        apiService.getInstructeurIndisponibilites(instructeurId, { actif: true }),
        apiService.getInstructeurPlanningAnomalies(instructeurId).catch(() => null),
      ]);
      setModeHoraires((prev) => normalizeModeHoraires(config.modeHoraires, prev));
      setHoraires(config.horaires || []);
      setCommerceHoraires(config.commerceHoraires || []);
      setIndisponibilites(indispoData.indisponibilites || []);
      setPlanningWarning(anomalies?.count ? anomalies : null);
    } catch (err) {
      toast.error(err.message || 'Impossible de charger le planning');
    } finally {
      setPlanningLoading(false);
    }
  }, [instructeurId, toast]);

  useEffect(() => {
    loadPlanning();
  }, [loadPlanning]);

  const plagesByDay = useMemo(() => {
    const map = {};
    for (const j of JOURS) map[j.value] = [];
    for (const h of horaires) {
      if (!map[h.jourSemaine]) map[h.jourSemaine] = [];
      map[h.jourSemaine].push(h);
    }
    return map;
  }, [horaires]);

  const commerceByDay = useMemo(() => {
    const map = {};
    for (const j of JOURS) map[j.value] = [];
    for (const h of commerceHoraires) {
      if (!map[h.jourSemaine]) map[h.jourSemaine] = [];
      map[h.jourSemaine].push(h);
    }
    return map;
  }, [commerceHoraires]);

  const applyWarning = (warning) => {
    if (warning?.count) {
      setPlanningWarning(warning);
      toast.warning(formatWarningToast(warning));
    } else {
      setPlanningWarning(null);
    }
  };

  const handleModeChange = async (nextMode) => {
    if (!instructeurId || nextMode === modeHoraires || planningLoading) return;
    try {
      setPlanningLoading(true);
      const res = await apiService.updateInstructeurModeHoraires(instructeurId, nextMode);
      setModeHoraires(
        normalizeModeHoraires(res.instructeur?.modeHoraires, nextMode),
      );
      applyWarning(res.warning);
      toast.success(
        nextMode === 'COMMERCE'
          ? 'Horaires du commerce utilisés'
          : 'Horaires personnalisés activés',
      );
      notifyPlanningChanged();
    } catch (err) {
      toast.error(err.message || 'Changement de mode impossible');
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleAddPlage = async (jourSemaine) => {
    const draft = newPlageByDay[jourSemaine] || { heureDebut: '09:00', heureFin: '12:00' };
    try {
      setPlanningLoading(true);
      const res = await apiService.createInstructeurHoraire(instructeurId, {
        jourSemaine,
        heureDebut: draft.heureDebut,
        heureFin: draft.heureFin,
      });
      applyWarning(res.warning);
      setNewPlageByDay((prev) => ({
        ...prev,
        [jourSemaine]: { heureDebut: '09:00', heureFin: '12:00' },
      }));
      await loadPlanning();
      notifyPlanningChanged();
    } catch (err) {
      toast.error(err.message || 'Ajout de plage impossible');
    } finally {
      setPlanningLoading(false);
    }
  };

  const startEditPlage = (h) => {
    setEditingHoraireId(h.id);
    setEditDraft({ heureDebut: h.heureDebut, heureFin: h.heureFin });
  };

  const cancelEditPlage = () => {
    setEditingHoraireId(null);
    setEditDraft({ heureDebut: '', heureFin: '' });
  };

  const handleUpdatePlage = async (horaireId, jourSemaine) => {
    try {
      setPlanningLoading(true);
      const res = await apiService.updateInstructeurHoraire(instructeurId, horaireId, {
        jourSemaine,
        heureDebut: editDraft.heureDebut,
        heureFin: editDraft.heureFin,
      });
      applyWarning(res.warning);
      cancelEditPlage();
      await loadPlanning();
      notifyPlanningChanged();
      toast.success('Plage mise à jour');
    } catch (err) {
      toast.error(err.message || 'Modification impossible');
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleDeletePlage = async (horaireId) => {
    try {
      setPlanningLoading(true);
      const res = await apiService.deleteInstructeurHoraire(instructeurId, horaireId);
      applyWarning(res.warning);
      await loadPlanning();
      notifyPlanningChanged();
    } catch (err) {
      toast.error(err.message || 'Suppression impossible');
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleSaveAbsence = async (e) => {
    e.preventDefault();
    let dateDebut = null;
    let dateFin = null;

    if (absenceForm.durationMode === 'fullDay') {
      if (!absenceForm.dateDebutYmd || !absenceForm.dateFinYmd) {
        toast.error('Indiquez la date de début et la date de fin.');
        return;
      }
      if (compareYmd(absenceForm.dateFinYmd, absenceForm.dateDebutYmd) < 0) {
        toast.error('La date de fin doit être postérieure ou égale à la date de début.');
        return;
      }
      dateDebut = parisDayStartIso(absenceForm.dateDebutYmd);
      dateFin = parisDayEndIso(absenceForm.dateFinYmd);
    } else {
      if (!absenceForm.partialDateYmd || !absenceForm.partialHeureDebut || !absenceForm.partialHeureFin) {
        toast.error('Indiquez la date, l’heure de début et l’heure de fin.');
        return;
      }
      const startMin = parseMinutes(absenceForm.partialHeureDebut);
      const endMin = parseMinutes(absenceForm.partialHeureFin);
      if (startMin == null || endMin == null || endMin <= startMin) {
        toast.error('L’heure de fin doit être après l’heure de début.');
        return;
      }
      dateDebut = parisWallClockIso(absenceForm.partialDateYmd, absenceForm.partialHeureDebut);
      dateFin = parisWallClockIso(absenceForm.partialDateYmd, absenceForm.partialHeureFin);
    }

    if (!dateDebut || !dateFin) {
      toast.error('Impossible de construire les dates. Vérifiez les champs saisis.');
      return;
    }
    if (new Date(dateFin).getTime() <= new Date(dateDebut).getTime()) {
      toast.error('La fin doit être strictement après le début.');
      return;
    }

    const payload = {
      dateDebut,
      dateFin,
      motif: absenceForm.motif.trim() || null,
      type: absenceForm.type.trim() || null,
    };
    try {
      setPlanningLoading(true);
      let res;
      if (editingAbsenceId) {
        res = await apiService.updateInstructeurIndisponibilite(
          instructeurId,
          editingAbsenceId,
          payload,
        );
      } else {
        res = await apiService.createInstructeurIndisponibilite(instructeurId, payload);
      }
      applyWarning(res.warning);
      setAbsenceForm(emptyAbsenceForm);
      setEditingAbsenceId(null);
      setShowAbsenceForm(false);
      await loadPlanning();
      notifyPlanningChanged();
      toast.success(editingAbsenceId ? 'Absence mise à jour' : 'Absence ajoutée');
    } catch (err) {
      toast.error(err.message || 'Enregistrement absence impossible');
    } finally {
      setPlanningLoading(false);
    }
  };

  const handleDeleteAbsence = async (indisponibiliteId) => {
    try {
      setPlanningLoading(true);
      await apiService.deleteInstructeurIndisponibilite(instructeurId, indisponibiliteId);
      await loadPlanning();
      notifyPlanningChanged();
      toast.success('Absence supprimée');
    } catch (err) {
      toast.error(err.message || 'Suppression impossible');
    } finally {
      setPlanningLoading(false);
    }
  };

  const startEditAbsence = (a) => {
    setEditingAbsenceId(a.id);
    setShowAbsenceForm(true);
    const durationMode = detectDurationModeFromIndispo(a);
    if (durationMode === 'fullDay') {
      setAbsenceForm({
        durationMode: 'fullDay',
        dateDebutYmd: parisYmdFromIso(a.dateDebut),
        dateFinYmd: parisYmdFromIso(a.dateFin || a.dateDebut),
        partialDateYmd: '',
        partialHeureDebut: '09:00',
        partialHeureFin: '12:00',
        motif: a.motif || '',
        type: a.type || '',
      });
    } else {
      setAbsenceForm({
        durationMode: 'partial',
        dateDebutYmd: '',
        dateFinYmd: '',
        partialDateYmd: parisYmdFromIso(a.dateDebut),
        partialHeureDebut: parisTimeHmFromIso(a.dateDebut),
        partialHeureFin: parisTimeHmFromIso(a.dateFin),
        motif: a.motif || '',
        type: a.type || '',
      });
    }
  };

  if (!instructeurId) {
    return null;
  }

  if (mode === 'absences') {
    return (
      <div className="space-y-4">
        {planningWarning?.count > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {planningWarning.warningMessage ||
              `${planningWarning.count} rendez-vous sont à replanifier`}
          </div>
        )}

        {!showAbsenceForm && !editingAbsenceId && (
          <button
            type="button"
            onClick={() => {
              setEditingAbsenceId(null);
              setAbsenceForm(emptyAbsenceForm);
              setShowAbsenceForm(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            Ajouter une absence
          </button>
        )}

        {(showAbsenceForm || editingAbsenceId) && (
          <form onSubmit={handleSaveAbsence} className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-sm font-semibold text-gray-800">
              {editingAbsenceId ? 'Modifier l’absence' : 'Nouvelle absence'}
            </div>

            <fieldset className="space-y-2">
              <legend className="text-xs font-bold uppercase tracking-wide text-gray-500">
                Type de durée
              </legend>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
                <input
                  type="radio"
                  name="absence-duration-mode"
                  checked={absenceForm.durationMode === 'fullDay'}
                  onChange={() =>
                    setAbsenceForm((prev) => ({ ...prev, durationMode: 'fullDay' }))
                  }
                />
                Journée entière / plusieurs jours
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
                <input
                  type="radio"
                  name="absence-duration-mode"
                  checked={absenceForm.durationMode === 'partial'}
                  onChange={() =>
                    setAbsenceForm((prev) => ({ ...prev, durationMode: 'partial' }))
                  }
                />
                Quelques heures
              </label>
            </fieldset>

            {absenceForm.durationMode === 'fullDay' ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Date de début</label>
                  <input
                    type="date"
                    value={absenceForm.dateDebutYmd}
                    onChange={(e) =>
                      setAbsenceForm((prev) => ({ ...prev, dateDebutYmd: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Date de fin</label>
                  <input
                    type="date"
                    value={absenceForm.dateFinYmd}
                    onChange={(e) =>
                      setAbsenceForm((prev) => ({ ...prev, dateFinYmd: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Date</label>
                  <input
                    type="date"
                    value={absenceForm.partialDateYmd}
                    onChange={(e) =>
                      setAbsenceForm((prev) => ({ ...prev, partialDateYmd: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Début</label>
                  <input
                    type="time"
                    value={absenceForm.partialHeureDebut}
                    onChange={(e) =>
                      setAbsenceForm((prev) => ({ ...prev, partialHeureDebut: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Fin</label>
                  <input
                    type="time"
                    value={absenceForm.partialHeureFin}
                    onChange={(e) =>
                      setAbsenceForm((prev) => ({ ...prev, partialHeureFin: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                  />
                </div>
              </div>
            )}

            <input
              type="text"
              value={absenceForm.motif}
              onChange={(e) => setAbsenceForm((prev) => ({ ...prev, motif: e.target.value }))}
              placeholder="Motif (ex. Congés, Indisponibilité…)"
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={absenceForm.type}
              onChange={(e) => setAbsenceForm((prev) => ({ ...prev, type: e.target.value }))}
              placeholder="Type (optionnel, ex. CONGE)"
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                onClick={() => {
                  setEditingAbsenceId(null);
                  setAbsenceForm(emptyAbsenceForm);
                  setShowAbsenceForm(false);
                }}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={planningLoading}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {editingAbsenceId ? 'Mettre à jour' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}

        {indisponibilites.length === 0 ? (
          <p className="text-sm text-gray-500">Aucune absence enregistrée.</p>
        ) : (
          <ul className={wideLayout ? 'grid gap-2 md:grid-cols-2' : 'space-y-2'}>
            {indisponibilites.map((a) => {
              const { title, detail, motif } = formatAbsenceRange(
                a.dateDebut,
                a.dateFin,
                a.motif,
                a.type,
              );
              return (
                <li
                  key={a.id}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{title}</div>
                      <div className="mt-0.5 whitespace-pre-line text-sm text-gray-600">{detail}</div>
                      {motif && <div className="mt-1 text-xs text-gray-500">{motif}</div>}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs font-medium text-primary-600 hover:underline"
                        onClick={() => startEditAbsence(a)}
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        className="text-xs font-medium text-red-600 hover:underline"
                        onClick={() => handleDeleteAbsence(a.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {planningLoading && <p className="text-xs text-gray-500">Chargement du planning…</p>}
      {planningWarning?.count > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {planningWarning.warningMessage ||
            `${planningWarning.count} rendez-vous sont à replanifier`}
        </div>
      )}
      <fieldset className="space-y-2 rounded-xl border border-gray-100 bg-gray-50 p-4">
        <legend className="px-1 text-sm font-semibold text-gray-800">Mode</legend>
        {/*
          Ne pas disabled={planningLoading} sur les radios : sous Windows/Chrome
          un radio disabled perd souvent l’aspect « checked » même si checked=true
          → pastille visuellement vide alors que le mode PERSONNALISE est actif.
          handleModeChange ignore déjà les clics pendant un mode identique / erreur.
        */}
        <label
          className={`flex items-center gap-2 text-sm ${
            planningLoading ? 'cursor-wait opacity-80' : 'cursor-pointer'
          }`}
        >
          <input
            type="radio"
            name={`modeHoraires-${instructeurId}`}
            checked={modeHoraires === 'COMMERCE'}
            onChange={() => {
              if (!planningLoading) handleModeChange('COMMERCE');
            }}
          />
          Horaires du commerce
        </label>
        <label
          className={`flex items-center gap-2 text-sm ${
            planningLoading ? 'cursor-wait opacity-80' : 'cursor-pointer'
          }`}
        >
          <input
            type="radio"
            name={`modeHoraires-${instructeurId}`}
            checked={modeHoraires === 'PERSONNALISE'}
            onChange={() => {
              if (!planningLoading) handleModeChange('PERSONNALISE');
            }}
          />
          Planning personnalisé
        </label>
      </fieldset>

      {modeHoraires === 'COMMERCE' && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Horaires du commerce (lecture seule)
          </p>
          <div className={`grid gap-2 ${wideLayout ? 'md:grid-cols-2 xl:grid-cols-3' : 'sm:grid-cols-2'}`}>
            {JOURS.map((j) => (
              <PlanningDayCard
                key={j.value}
                label={j.label}
                plages={commerceByDay[j.value] || []}
              />
            ))}
          </div>
        </div>
      )}

      {modeHoraires === 'PERSONNALISE' && (
        <div
          className={`space-y-3 pr-1 ${
            wideLayout
              ? 'grid gap-3 lg:grid-cols-2 xl:max-h-[min(70vh,40rem)] xl:overflow-y-auto'
              : 'max-h-[min(60vh,32rem)] overflow-y-auto'
          }`}
        >
          {JOURS.map((j) => {
            const dayPlages = plagesByDay[j.value] || [];
            const draft = newPlageByDay[j.value] || { heureDebut: '09:00', heureFin: '12:00' };
            const commerceClosed = isCommerceClosedOnDay(commerceHoraires, j.value);
            return (
              <div key={j.value} className="rounded-xl border border-gray-200 bg-white p-3">
                <div className="text-sm font-semibold text-gray-900">{j.label}</div>
                {commerceClosed && (
                  <p className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
                    Le commerce est fermé ce jour. Ces horaires personnalisés ne permettront pas
                    nécessairement une réservation publique selon la configuration de la boutique.
                  </p>
                )}

                <div className="mt-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Plages enregistrées
                  </p>
                  {dayPlages.length === 0 ? (
                    <p className="mt-1 text-sm text-gray-500">Aucune plage — indisponible ce jour</p>
                  ) : (
                    <ul className="mt-1.5 space-y-0.5">
                      {dayPlages.map((h) => (
                        <li key={h.id} className="text-sm text-gray-700">
                          {editingHoraireId === h.id ? (
                            <div className="flex flex-wrap items-end gap-2 rounded-lg bg-gray-50 p-2">
                              <div>
                                <label className="mb-0.5 block text-xs text-gray-500">Début</label>
                                <input
                                  type="time"
                                  value={editDraft.heureDebut}
                                  onChange={(e) =>
                                    setEditDraft((prev) => ({ ...prev, heureDebut: e.target.value }))
                                  }
                                  className="rounded border border-gray-200 px-2 py-1 text-sm"
                                />
                              </div>
                              <div>
                                <label className="mb-0.5 block text-xs text-gray-500">Fin</label>
                                <input
                                  type="time"
                                  value={editDraft.heureFin}
                                  onChange={(e) =>
                                    setEditDraft((prev) => ({ ...prev, heureFin: e.target.value }))
                                  }
                                  className="rounded border border-gray-200 px-2 py-1 text-sm"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => handleUpdatePlage(h.id, j.value)}
                                className="rounded-lg bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white"
                              >
                                Enregistrer
                              </button>
                              <button
                                type="button"
                                onClick={cancelEditPlage}
                                className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs"
                              >
                                Annuler
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <span>
                                {h.heureDebut} → {h.heureFin}
                              </span>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  className="text-xs text-primary-600 hover:underline"
                                  onClick={() => startEditPlage(h)}
                                >
                                  Modifier
                                </button>
                                <button
                                  type="button"
                                  className="text-xs text-red-600 hover:underline"
                                  onClick={() => handleDeletePlage(h.id)}
                                >
                                  Supprimer
                                </button>
                              </div>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="mt-3 rounded-lg border border-dashed border-gray-300 bg-slate-50/80 p-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Ajouter une plage
                  </p>
                  <p className="mb-2 text-[11px] text-gray-500">
                    Valeurs proposées uniquement — non enregistrées tant que vous n&apos;ajoutez pas.
                  </p>
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <label className="mb-0.5 block text-xs text-gray-500">Début</label>
                      <input
                        type="time"
                        value={draft.heureDebut}
                        onChange={(e) =>
                          setNewPlageByDay((prev) => ({
                            ...prev,
                            [j.value]: { ...draft, heureDebut: e.target.value },
                          }))
                        }
                        className="rounded border border-gray-200 bg-white px-2 py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-0.5 block text-xs text-gray-500">Fin</label>
                      <input
                        type="time"
                        value={draft.heureFin}
                        onChange={(e) =>
                          setNewPlageByDay((prev) => ({
                            ...prev,
                            [j.value]: { ...draft, heureFin: e.target.value },
                          }))
                        }
                        className="rounded border border-gray-200 bg-white px-2 py-1 text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddPlage(j.value)}
                      className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
                    >
                      Ajouter
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
